import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { FileText, AlertTriangle, ShieldOff, PhoneOff, CreditCard, Clock, Lock, Mail } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const LORA = { fontFamily: "'Lora', Georgia, serif" };

const Section = ({ icon: Icon, title, children }) => (
  <section className="border-b border-[#DCD4C6] pb-8 mb-8 last:border-0 last:mb-0">
    <div className="flex items-start gap-3 mb-4">
      <div className="w-8 h-8 border border-[#DCD4C6] flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: "#F1EBE1", borderRadius: "2px" }}>
        <Icon size={14} strokeWidth={1.5} className="text-[#C17F59]" />
      </div>
      <h2 className="text-xl pt-1" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
        {title}
      </h2>
    </div>
    <div className="ml-11 space-y-3 text-sm text-[#5C554E] leading-relaxed" style={LORA}>
      {children}
    </div>
  </section>
);

const Alert = ({ children }) => (
  <div className="flex items-start gap-3 p-4 border border-amber-200 rounded-sm mb-4"
    style={{ background: "#FFFBF0" }}>
    <AlertTriangle size={15} strokeWidth={1.5} className="text-amber-600 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-800 font-medium" style={LORA}>{children}</p>
  </div>
);

export default function TermsPage() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16">

        {/* Header */}
        <div className="mb-12 animate-fade-in-up">
          <p className="section-label mb-3">Documents légaux</p>
          <h1 className="text-4xl sm:text-5xl mb-4" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
            Conditions Générales de Vente
          </h1>
          <div className="flex flex-wrap gap-4 text-xs text-[#9A8F86] mt-2" style={LORA}>
            <span>Voyage-r · Diego Lastra</span>
            <span>·</span>
            <span>Mise à jour : mai 2026</span>
            <span>·</span>
            <span>Applicable à tout achat réalisé sur voyage-r.fr</span>
          </div>
        </div>

        {/* Warning box */}
        <Alert>
          Voyage-r est un service de conseil en itinéraire — pas une agence de voyage. Diego Lastra
          n'effectue aucune réservation en votre nom et ne dispose d'aucun agrément d'agence de voyage.
        </Alert>

        <div className="card-surface animate-fade-in-up" style={{ animationDelay: "0.1s" }}>

          {/* Art. 1 — Objet */}
          <Section icon={FileText} title="Article 1 — Objet et nature de la prestation">
            <p>
              Voyage-r est un service de <strong className="text-[#2B2623]">conseil personnalisé en itinéraire de voyage</strong> proposé
              par Diego Lastra, conseiller voyage indépendant. La prestation consiste exclusivement en la
              fourniture d'un document numérique d'itinéraire sur mesure, élaboré à partir des informations
              transmises par le client via le formulaire en ligne, puis validé par Diego Lastra avant livraison.
            </p>
            <p>
              Voyage-r <strong className="text-[#2B2623]">n'est pas une agence de voyage</strong> au sens de l'article L.211-1 du Code du
              tourisme. Diego Lastra n'est ni opérateur de voyages, ni organisateur de circuits, ni titulaire
              d'un agrément Atout France. La prestation est de nature consultative uniquement.
            </p>
          </Section>

          {/* Art. 2 — Ce qui n'est PAS inclus */}
          <Section icon={ShieldOff} title="Article 2 — Périmètre exclu de la prestation">
            <p className="font-medium text-[#2B2623]">La prestation Voyage-r n'inclut en aucun cas :</p>
            <ul className="space-y-2 mt-2">
              {[
                "La réservation ou l'achat de billets d'avion, de train ou de tout autre titre de transport ;",
                "La réservation d'hébergements, d'hôtels, de locations ou de toute autre prestation d'hébergement ;",
                "La souscription ou la gestion d'assurances voyage ;",
                "L'obtention de visas, autorisations de voyage ou tout document administratif ;",
                "La gestion de transferts, d'excursions ou d'activités touristiques ;",
                "Toute assistance ou support en cas d'urgence, d'annulation, de retard ou d'incident survenant avant, pendant ou après le voyage.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#C17F59] font-bold mt-0.5 flex-shrink-0">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3">
              Le client reste <strong className="text-[#2B2623]">seul responsable</strong> de l'ensemble des démarches de réservation,
              de la vérification des conditions d'entrée dans le pays de destination (visa, vaccination, documents
              d'identité) et de la souscription d'une assurance voyage adaptée.
            </p>
          </Section>

          {/* Art. 3 — Aucune assistance d'urgence */}
          <Section icon={PhoneOff} title="Article 3 — Absence d'assistance d'urgence">
            <Alert>
              Voyage-r ne fournit aucune assistance d'urgence, quelle qu'elle soit, avant, pendant ou après
              le voyage. En cas d'urgence, le client doit contacter les autorités locales, son assureur voyage
              ou le centre d'appel d'urgence consulaire du pays concerné.
            </Alert>
            <p>
              Diego Lastra ne peut être joint en dehors des horaires de disponibilité indiqués sur le site
              et ne saurait être tenu responsable d'événements survenant lors du voyage (accident, maladie,
              incident de transport, force majeure, etc.).
            </p>
          </Section>

          {/* Art. 4 — Prix et paiement */}
          <Section icon={CreditCard} title="Article 4 — Prix et conditions de paiement">
            <p>
              Les tarifs des offres (Essentiel, Confort, Sérénité) sont affichés en euros TTC sur la page de
              paiement, préalablement à tout achat. Le paiement est effectué en une seule fois, via la
              plateforme sécurisée <strong className="text-[#2B2623]">Stripe</strong>. Aucun abonnement, aucun frais récurrent.
            </p>
            <p>
              Le paiement est exigible et définitif dès la validation de la commande. Conformément à
              l'article L.221-28 du Code de la consommation, le droit de rétractation de 14 jours ne
              s'applique pas aux contenus numériques fournis immédiatement après paiement et dont la
              livraison a débuté avec l'accord exprès du client, matérialisé par la case à cocher présente
              sur la page de commande.
            </p>
            <p>
              En cas d'échec du paiement ou de litige bancaire, Voyage-r se réserve le droit de suspendre
              l'accès à l'itinéraire.
            </p>
          </Section>

          {/* Art. 5 — Livraison et durée d'accès */}
          <Section icon={Clock} title="Article 5 — Livraison et durée d'accès">
            <p>
              L'itinéraire est accessible immédiatement après confirmation du paiement, depuis l'espace
              personnel du client sur voyage-r.fr. Aucun document n'est envoyé par courrier postal.
            </p>
            <p>
              L'accès à l'itinéraire est garanti pour la <strong className="text-[#2B2623]">durée du voyage</strong> (de la date de départ à
              la date de retour) <strong className="text-[#2B2623]">augmentée de 15 jours</strong>. Passé ce délai, l'accès expire automatiquement.
              Aucun remboursement ne sera effectué du fait de cette expiration.
            </p>
            <p>
              Voyage-r se réserve le droit de modifier les informations pratiques de l'itinéraire (conditions
              d'entrée, météo, etc.) sans que cela constitue un motif de remboursement.
            </p>
          </Section>

          {/* Art. 6 — Responsabilité */}
          <Section icon={Lock} title="Article 6 — Limitation de responsabilité">
            <p>
              Les informations contenues dans l'itinéraire sont fournies à titre indicatif, sur la base
              des données disponibles au moment de sa rédaction. Elles ne constituent pas une garantie de
              disponibilité, de prix ou d'exactitude. Diego Lastra décline toute responsabilité pour :
            </p>
            <ul className="space-y-1 mt-2">
              {[
                "Les modifications de visa ou d'entrée sur le territoire décidées par les autorités locales ;",
                "Les variations de prix des vols, hébergements ou prestations tierces ;",
                "Les événements imprévisibles (grèves, catastrophes naturelles, épidémies, guerre) ;",
                "Les préjudices résultant d'une mauvaise interprétation ou d'une utilisation incorrecte de l'itinéraire.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#C17F59] font-bold mt-0.5 flex-shrink-0">—</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-3">
              En tout état de cause, la responsabilité de Voyage-r est limitée au montant de la prestation
              effectivement payée par le client.
            </p>
          </Section>

          {/* Art. 7 — Données personnelles */}
          <Section icon={Mail} title="Article 7 — Données personnelles et contact">
            <p>
              Les données collectées (nom, adresse email, préférences de voyage) sont utilisées exclusivement
              pour la réalisation de la prestation et l'envoi des communications relatives au voyage commandé.
              Elles ne sont ni vendues, ni transmises à des tiers. Conformément au RGPD, vous disposez d'un
              droit d'accès, de rectification et de suppression auprès de{" "}
              <a href="mailto:diego@voyage-r.fr" className="text-[#C17F59] hover:underline">diego@voyage-r.fr</a>.
            </p>
            <p>
              Pour toute question relative à une commande :{" "}
              <a href="mailto:diego@voyage-r.fr" className="text-[#C17F59] hover:underline">diego@voyage-r.fr</a>.
            </p>
          </Section>

          {/* Art. 8 — Droit applicable */}
          <section className="pt-2">
            <p className="text-xs text-[#9A8F86] italic leading-relaxed" style={LORA}>
              <strong style={{ color: "#5C554E" }}>Article 8 — Droit applicable.</strong>{" "}
              Les présentes CGV sont soumises au droit français. En cas de litige, et après tentative
              de résolution amiable, le tribunal compétent sera celui du lieu du domicile du vendeur.
              Le client peut également recourir à la médiation de la consommation via la plateforme
              européenne de règlement en ligne des litiges :{" "}
              <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer"
                className="text-[#C17F59] hover:underline">ec.europa.eu/consumers/odr</a>.
            </p>
          </section>

        </div>

        {/* Footer nav */}
        <div className="flex items-center gap-6 mt-10 text-xs text-[#9A8F86]" style={LORA}>
          <Link to="/" className="hover:text-[#C17F59] transition-colors">← Retour à l'accueil</Link>
          <span>·</span>
          <a href="mailto:diego@voyage-r.fr" className="hover:text-[#C17F59] transition-colors">Contact</a>
        </div>

      </main>
    </div>
  );
}
